/**
 * Bulk Role Operations Component
 * Epic #17: Bulk Role Operations
 *
 * Provides UI for bulk role assignment and revocation with CSV upload,
 * preview, and results download functionality using Carbon Design System
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Column,
  Tile,
  ContentSwitcher,
  Switch,
  FileUploader,
  Button,
  InlineNotification,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  ProgressIndicator,
  ProgressStep,
  Modal,
  TextInput,
  Checkbox,
  Loading,
  InlineLoading,
} from '@carbon/react';
import {
  Upload,
  Download,
  View,
  CheckmarkFilled,
  WarningFilled,
  ErrorFilled,
  TrashCan,
  DocumentBlank,
} from '@carbon/icons-react';
import axios, { AxiosError } from 'axios';
import { useAuthStore, tokenManager } from '../../store/authStore';
import type {
  BulkRoleAssignmentRequest,
  BulkRoleAssignmentResponse,
  BulkOperationPreviewResponse,
  BulkOperationRowResult,
  BulkRoleRevocationFilter,
  BulkRoleAssignmentRow,
} from '../../../../shared/interfaces/rbac.interface';
import styles from './BulkRoleOperations.module.scss';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface BulkRoleOperationsProps {}

const BulkRoleOperations: React.FC<BulkRoleOperationsProps> = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Get token from tokenManager
  const getAuthToken = (): string | null => {
    const tokens = tokenManager.getTokens();
    return tokens.accessToken;
  };

  // State management
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [operation, setOperation] = useState<'assign' | 'revoke'>('assign');
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<BulkOperationPreviewResponse | null>(null);
  const [results, setResults] = useState<BulkRoleAssignmentResponse | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Revocation filter state
  const [revocationFilter, setRevocationFilter] = useState<BulkRoleRevocationFilter>({
    role_name: '',
    institution_id: '',
    institution_type: undefined,
    before_date: '',
    expired_only: false,
  });

  /**
   * Handle file upload
   */
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
      setError(null);
      setResults(null);
      setPreviewData(null);
    }
  };

  /**
   * Download CSV template
   */
  const handleDownloadTemplate = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/v1/rbac/bulk-operations/template`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bulk_role_assignment_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      const error = err as AxiosError<{ message: string }>;
      setError(error.response?.data?.message || 'Failed to download template');
    }
  };

  /**
   * Preview bulk operation
   */
  const handlePreview = async () => {
    if (!selectedFile && operation === 'assign') {
      setError('Please select a CSV file');
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let requestData: BulkRoleAssignmentRequest;

      if (operation === 'assign') {
        const formData = new FormData();
        formData.append('file', selectedFile!);

        const csvText = await selectedFile!.text();
        const assignments = parseCSV(csvText);

        requestData = {
          operation: 'preview',
          assignments,
          dry_run: true,
        };
      } else {
        requestData = {
          operation: 'preview',
          revocation_filter: revocationFilter,
          dry_run: true,
        };
      }

      const response = await axios.post(`${API_BASE_URL}/v1/rbac/bulk-operations`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      setPreviewData(response.data);
      setShowPreviewModal(true);
      setCurrentStep(1);
    } catch (err) {
      const error = err as AxiosError<{ message: string }>;
      setError(error.response?.data?.message || 'Failed to preview operation');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Execute bulk operation
   */
  const handleExecute = async () => {
    const token = getAuthToken();
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);
    setShowPreviewModal(false);

    try {
      let requestData: BulkRoleAssignmentRequest;

      if (operation === 'assign') {
        const csvText = await selectedFile!.text();
        const assignments = parseCSV(csvText);

        requestData = {
          operation: 'assign',
          assignments,
          dry_run: false,
        };
      } else {
        requestData = {
          operation: 'revoke',
          revocation_filter: revocationFilter,
          dry_run: false,
        };
      }

      const response = await axios.post(`${API_BASE_URL}/v1/rbac/bulk-operations`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      setResults(response.data);
      setCurrentStep(2);
    } catch (err) {
      const error = err as AxiosError<{ message: string }>;
      setError(error.response?.data?.message || 'Failed to execute operation');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Download results CSV
   */
  const handleDownloadResults = async () => {
    if (!results) return;

    const token = getAuthToken();
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/v1/rbac/bulk-operations/download-results`,
        { results: results.results },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bulk_operation_results.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      const error = err as AxiosError<{ message: string }>;
      setError(error.response?.data?.message || 'Failed to download results');
    }
  };

  /**
   * Parse CSV text
   */
  const parseCSV = (csvText: string): BulkRoleAssignmentRow[] => {
    const lines = csvText.trim().split('\n');
    if (lines.length === 0) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const rows: BulkRoleAssignmentRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (!values[0]) continue;

      const row: BulkRoleAssignmentRow = {
        email: values[headers.indexOf('email')] || '',
        role_name: values[headers.indexOf('role_name')] || '',
      };

      const institutionId = values[headers.indexOf('institution_id')];
      if (institutionId) row.institution_id = institutionId;

      const institutionName = values[headers.indexOf('institution_name')];
      if (institutionName) row.institution_name = institutionName;

      const institutionType = values[headers.indexOf('institution_type')];
      // Validate institution_type against allowed values
      if (institutionType && ['school', 'district', 'class', 'platform'].includes(institutionType)) {
        row.institution_type = institutionType as 'school' | 'district' | 'class' | 'platform';
      }

      const effectiveDate = values[headers.indexOf('effective_date')];
      if (effectiveDate) row.effective_date = effectiveDate;

      const expirationDate = values[headers.indexOf('expiration_date')];
      if (expirationDate) row.expiration_date = expirationDate;

      rows.push(row);
    }

    return rows;
  };

  /**
   * Reset operation
   */
  const handleReset = () => {
    setSelectedFile(null);
    setError(null);
    setPreviewData(null);
    setResults(null);
    setCurrentStep(0);
    setRevocationFilter({
      role_name: '',
      institution_id: '',
      institution_type: undefined,
      before_date: '',
      expired_only: false,
    });
  };

  // Table headers for preview
  const previewHeaders = [
    { key: 'row', header: 'Row' },
    { key: 'email', header: 'Email' },
    { key: 'role_name', header: 'Role' },
    { key: 'action', header: 'Action' },
    { key: 'status', header: 'Status' },
  ];

  // Table headers for results
  const resultsHeaders = [
    { key: 'row', header: 'Row' },
    { key: 'email', header: 'Email' },
    { key: 'role_name', header: 'Role' },
    { key: 'success', header: 'Status' },
    { key: 'message', header: 'Message' },
  ];

  return (
    <Grid fullWidth className={styles.bulkRoleOperations}>
      {/* Header */}
      <Column lg={16}>
        <div className={styles.headerContainer}>
          <h1 className={styles.pageTitle}>Bulk Role Operations</h1>
          <p className={styles.pageDescription}>
            Efficiently manage roles for large user populations with CSV upload and preview
          </p>
        </div>
      </Column>

      {/* Progress Indicator */}
      <Column lg={16}>
        <Tile className={styles.progressContainer}>
          <ProgressIndicator currentIndex={currentStep} spaceEqually>
            <ProgressStep label="Upload & Configure" description="Select operation and upload data" />
            <ProgressStep label="Preview Changes" description="Review changes before applying" />
            <ProgressStep label="Results" description="Download results and verify" />
          </ProgressIndicator>
        </Tile>
      </Column>

      {/* Error Notification */}
      {error && (
        <Column lg={16}>
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
            className={styles.errorNotification}
          />
        </Column>
      )}

      {/* Main Content */}
      <Column lg={16}>
        <Tile className={styles.tileContainer}>
          {/* Operation Switcher */}
          <ContentSwitcher
            size="lg"
            selectedIndex={selectedTab}
            onChange={(data: any) => {
              setSelectedTab(data.index);
              setOperation(data.index === 0 ? 'assign' : 'revoke');
              handleReset();
            }}
            className={styles.operationSwitcher}
          >
            <Switch name="assign" text="Bulk Assign Roles" />
            <Switch name="revoke" text="Bulk Revoke Roles" />
          </ContentSwitcher>

          {/* Bulk Assign Panel */}
          {selectedTab === 0 && (
            <div className={styles.tabContent}>
              {currentStep === 0 && (
                <>
                  <h3 className={styles.sectionHeading}>Upload CSV File</h3>
                  <p className={styles.sectionDescription}>
                    Upload a CSV file with user emails and role assignments. Download the template to see the required
                    format.
                  </p>

                  <Button
                    kind="tertiary"
                    renderIcon={Download}
                    onClick={handleDownloadTemplate}
                    className={styles.sectionDescription}
                    aria-label="Download CSV template for bulk role assignment"
                  >
                    Download CSV Template
                  </Button>

                  <FileUploader
                    labelTitle="CSV File"
                    labelDescription="Maximum file size is 10MB. CSV format only."
                    buttonLabel="Select file"
                    filenameStatus="edit"
                    accept={['.csv']}
                    onChange={handleFileUpload}
                    iconDescription="Remove file"
                    className={styles.sectionDescription}
                  />

                  {selectedFile && (
                    <Tag type="blue" className={styles.fileTag}>
                      <DocumentBlank size={16} className={styles.fileIcon} />
                      {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </Tag>
                  )}

                  <div className={styles.buttonGroup}>
                    <Button
                      kind="primary"
                      renderIcon={View}
                      onClick={handlePreview}
                      disabled={!selectedFile || loading}
                      aria-label="Preview bulk role assignment changes"
                    >
                      {loading ? 'Loading...' : 'Preview Changes'}
                    </Button>
                    <Button kind="secondary" onClick={handleReset}>
                      Reset
                    </Button>
                  </div>
                </>
              )}

              {currentStep === 2 && results && (
                <>
                  <h3 className={styles.sectionHeading}>Operation Results</h3>

                  <div className={styles.statsContainer}>
                    <Tile className={styles.statTile}>
                      <p className={styles.statLabel}>Total Processed</p>
                      <p className={styles.statValue}>{results.total}</p>
                    </Tile>
                    <Tile className={styles.statTile}>
                      <p className={styles.statLabel}>Succeeded</p>
                      <p className={styles.statValueSuccess}>{results.succeeded}</p>
                    </Tile>
                    <Tile className={styles.statTile}>
                      <p className={styles.statLabel}>Failed</p>
                      <p className={styles.statValueError}>{results.failed}</p>
                    </Tile>
                    <Tile className={styles.statTile}>
                      <p className={styles.statLabel}>Warnings</p>
                      <p className={styles.statValueWarning}>{results.warnings}</p>
                    </Tile>
                  </div>

                  <DataTable
                    rows={results.results.map((result, index) => ({
                      id: String(index),
                      row: result.row,
                      email: result.email || '',
                      role_name: result.role_name || '',
                      success: result.success,
                      message: result.error || result.warning || 'Success',
                    }))}
                    headers={resultsHeaders}
                    size="sm"
                  >
                    {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                      <TableContainer title="Detailed Results">
                        <Table {...getTableProps()}>
                          <TableHead>
                            <TableRow>
                              {headers.map(header => (
                                <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                  {header.header}
                                </TableHeader>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rows.map(row => (
                              <TableRow {...getRowProps({ row })} key={row.id}>
                                {row.cells.map(cell => (
                                  <TableCell key={cell.id}>
                                    {cell.info.header === 'success' ? (
                                      <Tag
                                        type={cell.value ? 'green' : 'red'}
                                        size="sm"
                                        renderIcon={cell.value ? CheckmarkFilled : ErrorFilled}
                                      >
                                        {cell.value ? 'Success' : 'Failed'}
                                      </Tag>
                                    ) : (
                                      cell.value
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </DataTable>

                  <div className={styles.actionsGroup}>
                    <Button
                      kind="primary"
                      renderIcon={Download}
                      onClick={handleDownloadResults}
                      aria-label="Download bulk operation results as CSV"
                    >
                      Download Results CSV
                    </Button>
                    <Button kind="secondary" onClick={handleReset}>
                      Start New Operation
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Bulk Revoke Panel */}
          {selectedTab === 1 && (
            <div className={styles.tabContent}>
              <h3 className={styles.sectionHeading}>Revocation Filters</h3>
              <p className={styles.sectionDescription}>
                Define filters to select which role assignments to revoke. Leave fields empty to skip that filter.
              </p>

              <div className={styles.formFieldGroup}>
                <TextInput
                  id="role-name"
                  labelText="Role Name (optional)"
                  placeholder="e.g., teacher, principal"
                  value={revocationFilter.role_name}
                  onChange={e => setRevocationFilter({ ...revocationFilter, role_name: e.target.value })}
                />

                <TextInput
                  id="institution-id"
                  labelText="Institution ID (optional)"
                  placeholder="e.g., SCHOOL-001"
                  value={revocationFilter.institution_id}
                  onChange={e => setRevocationFilter({ ...revocationFilter, institution_id: e.target.value })}
                />

                <TextInput
                  id="before-date"
                  labelText="Before Date (optional)"
                  placeholder="YYYY-MM-DD"
                  value={revocationFilter.before_date}
                  onChange={e => setRevocationFilter({ ...revocationFilter, before_date: e.target.value })}
                />

                <Checkbox
                  id="expired-only"
                  labelText="Expired assignments only"
                  checked={revocationFilter.expired_only}
                  onChange={e => setRevocationFilter({ ...revocationFilter, expired_only: e.target.checked })}
                />
              </div>

              <div className={styles.buttonGroup}>
                <Button
                  kind="primary"
                  renderIcon={View}
                  onClick={handlePreview}
                  disabled={loading}
                  aria-label="Preview bulk role revocations"
                >
                  {loading ? 'Loading...' : 'Preview Revocations'}
                </Button>
                <Button kind="secondary" onClick={handleReset}>
                  Reset
                </Button>
              </div>
            </div>
          )}
        </Tile>
      </Column>

      {/* Preview Modal */}
      <Modal
        open={showPreviewModal}
        modalHeading="Preview Changes"
        modalLabel="Bulk Operation Preview"
        primaryButtonText="Execute Operation"
        secondaryButtonText="Cancel"
        onRequestClose={() => setShowPreviewModal(false)}
        onRequestSubmit={handleExecute}
        size="lg"
      >
        {previewData && (
          <div>
            <p className={styles.modalDescription}>Review the changes before executing the bulk operation.</p>

            <div className={styles.statsContainer}>
              <Tile className={styles.statTile}>
                <p className={styles.statLabel}>Total Rows</p>
                <p className={styles.statValue}>{previewData.total_rows}</p>
              </Tile>
              <Tile className={styles.statTile}>
                <p className={styles.statLabel}>Will Succeed</p>
                <p className={styles.statValueSuccess}>{previewData.will_succeed}</p>
              </Tile>
              <Tile className={styles.statTile}>
                <p className={styles.statLabel}>Will Fail</p>
                <p className={styles.statValueError}>{previewData.will_fail}</p>
              </Tile>
              <Tile className={styles.statTile}>
                <p className={styles.statLabel}>Warnings</p>
                <p className={styles.statValueWarning}>{previewData.has_warnings}</p>
              </Tile>
            </div>

            <DataTable
              rows={previewData.preview_items.slice(0, 100).map((item, index) => ({
                id: String(index),
                row: item.row,
                email: item.email,
                role_name: item.role_name,
                action: item.action,
                status: item.will_succeed ? 'Success' : 'Fail',
              }))}
              headers={previewHeaders}
              size="sm"
            >
              {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                <TableContainer
                  title={`Preview (showing first ${Math.min(100, previewData.preview_items.length)} items)`}
                >
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow {...getRowProps({ row })} key={row.id}>
                          {row.cells.map(cell => (
                            <TableCell key={cell.id}>
                              {cell.info.header === 'status' ? (
                                <Tag type={cell.value === 'Success' ? 'green' : 'red'} size="sm">
                                  {cell.value}
                                </Tag>
                              ) : cell.info.header === 'action' ? (
                                <Tag type="blue" size="sm">
                                  {cell.value}
                                </Tag>
                              ) : (
                                cell.value
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>
          </div>
        )}

        {loading && (
          <div className={styles.loadingContainer}>
            <Loading description="Loading preview..." />
          </div>
        )}
      </Modal>
    </Grid>
  );
};

export default BulkRoleOperations;
